/* eslint-disable */

export const THEME_STORAGE_KEY = "app.theme";
export const DEFAULT_THEME_ID = "htb";

/* ---------------------------------------------------------------
   LIGHT / DARK MODE
   Independent of the color theme. Stored separately and applied as
   document.documentElement[data-mode="light" | "dark"]. The dark
   palette overlay lives in main.scss under :root[data-mode="dark"].
   ---------------------------------------------------------------*/
export const MODE_STORAGE_KEY = "app.mode";
export const MODES = { LIGHT: "light", DARK: "dark" };
export const DEFAULT_MODE = MODES.LIGHT;

export const isValidMode = (mode) =>
  mode === MODES.LIGHT || mode === MODES.DARK;

// Global lock to prevent overlapping view transitions during rapid switching
let isTransitioning = false;

export const applyMode = (mode, coords = null, skipAnimation = false) => {
  const next = isValidMode(mode) ? mode : DEFAULT_MODE;

  const performModeChange = () => {
    if (typeof document !== "undefined" && document.documentElement) {
      if (coords && coords.x !== undefined) {
        document.documentElement.style.setProperty("--wipe-x", `${coords.x}px`);
        document.documentElement.style.setProperty("--wipe-y", `${coords.y}px`);
      } else {
        document.documentElement.style.setProperty("--wipe-x", "0%");
        document.documentElement.style.setProperty("--wipe-y", "100%");
      }
      document.documentElement.setAttribute("data-mode", next);
    }
    try {
      localStorage.setItem(MODE_STORAGE_KEY, next);
    } catch (error) {
      // localStorage may be unavailable; fail silently
    }
    if (typeof window !== "undefined") {
      window.dispatchEvent(
        new CustomEvent("themeChange", { detail: { mode: next } })
      );
    }
  };

  // If View Transition API is available and not skipped, use it for a smooth cinematic shift
  if (
    !skipAnimation &&
    !isTransitioning &&
    typeof document !== "undefined" &&
    document.startViewTransition &&
    !window.matchMedia("(prefers-reduced-motion: reduce)").matches
  ) {
    isTransitioning = true;
    const transition = document.startViewTransition(() => performModeChange());
    transition.finished.finally(() => {
      isTransitioning = false;
    });
  } else {
    performModeChange();
  }

  return next;
};

export const getStoredMode = () => {
  try {
    const value = localStorage.getItem(MODE_STORAGE_KEY);
    return isValidMode(value) ? value : DEFAULT_MODE;
  } catch (error) {
    return DEFAULT_MODE;
  }
};

export const toggleMode = (coords = null) =>
  applyMode(getStoredMode() === MODES.DARK ? MODES.LIGHT : MODES.DARK, coords);

export const initMode = () => {
  applyMode(getStoredMode(), null, true);
};

export const THEME_OPTIONS = [
  // Brand
  { value: "htb", label: "HTB" },
  { value: "equipped-cloud", label: "Equipped Cloud" },
  { value: "executive-blue", label: "Executive Blue" },
  // Blues & Teals & Green
  { value: "equipped", label: "Equipped" },
  { value: "forest-mist", label: "Forest Mist" },
  { value: "nordic-light", label: "Nordic Light" },
  { value: "deep-petrol", label: "Deep Petrol" },
  // Purples
  { value: "indigo", label: "Indigo" },
  { value: "midnight-violet", label: "Midnight Violet" },
  { value: "blush-quartz", label: "Blush Quartz" },
  // Reds & Pinks
  { value: "crimson", label: "Crimson" },
  // Warm Neutrals
  { value: "amber", label: "Amber" },
  { value: "copper-dusk", label: "Copper Dusk" },
  // Dark Neutrals
  { value: "slate", label: "Slate" },
];

export const THEME_PREVIEWS = {
  // Brand
  htb: ["#122B4F", "#1B3F6F", "#D4A24A", "#ECDCB0"],
  "equipped-cloud": ["#ffffff", "#f5f7fa", "#1976d2", "#90caf9"],
  "executive-blue": ["#1e293b", "#0f172a", "#2563eb", "#f1f5f9"],
  // Blues & Teals  green
  equipped: ["#1C3D35", "#030B09", "#6EBB78", "#E2E0E3"],
  "forest-mist": ["#083344", "#164E63", "#10B981", "#F0FDFA"],
  "nordic-light": ["#334155", "#475569", "#0d9488", "#f8fafc"],
  "deep-petrol": ["#0F3B3D", "#0B2E30", "#1F6F73", "#B8DEE0"],
  // Purples
  indigo: ["#263238", "#1a237e", "#3949ab", "#9fa8da"],
  "midnight-violet": ["#2a233a", "#1f1a2e", "#8e7cff", "#d1c4e9"],
  // "steel-mauve": ["#2E2A33", "#232029", "#7B6D8D", "#D8D2E3"],
  "blush-quartz": ["#3a2e39", "#2f2430", "#e57399", "#f8bbd0"],
  // Reds & Pinks
  crimson: ["#2a2a2a", "#f9eaea", "#d32f2f", "#f2b8b5"],
  // Warm Neutrals
  amber: ["#263238", "#37474f", "#ffb300", "#ffe082"],
  "copper-dusk": ["#3A2F2A", "#2C2320", "#C47A4A", "#E6CFC0"],
  // Dark Neutrals
  slate: ["#2e3440", "#242933", "#64748b", "#cbd5e1"],
};

export const isValidTheme = (themeId) =>
  Boolean(themeId) && THEME_OPTIONS.some((option) => option.value === themeId);

export const applyTheme = (themeId, coords = null, skipAnimation = false) => {
  if (typeof document === "undefined" || !document.documentElement) {
    return;
  }
  const id = isValidTheme(themeId) ? themeId : DEFAULT_THEME_ID;

  const performThemeChange = () => {
    if (coords && coords.x !== undefined) {
      document.documentElement.style.setProperty("--wipe-x", `${coords.x}px`);
      document.documentElement.style.setProperty("--wipe-y", `${coords.y}px`);
    } else {
      document.documentElement.style.setProperty("--wipe-x", "0%");
      document.documentElement.style.setProperty("--wipe-y", "100%");
    }
    document.documentElement.setAttribute("data-theme", id);
    try {
      localStorage.setItem(THEME_STORAGE_KEY, id);
    } catch (error) {
      // localStorage may be unavailable; fail silently
    }
    if (typeof window !== "undefined") {
      window.dispatchEvent(
        new CustomEvent("themeChange", { detail: { theme: id } })
      );
    }
  };

  // If View Transition API is available and not skipped, use it for a smooth cinematic shift
  if (
    !skipAnimation &&
    !isTransitioning &&
    document.startViewTransition &&
    !window.matchMedia("(prefers-reduced-motion: reduce)").matches
  ) {
    isTransitioning = true;
    const transition = document.startViewTransition(() => performThemeChange());
    transition.finished.finally(() => {
      isTransitioning = false;
    });
  } else {
    performThemeChange();
  }
};

export const getStoredTheme = () => {
  try {
    const value = localStorage.getItem(THEME_STORAGE_KEY);
    return isValidTheme(value) ? value : DEFAULT_THEME_ID;
  } catch (error) {
    return DEFAULT_THEME_ID;
  }
};

export const initTheme = () => {
  applyTheme(getStoredTheme(), null, true);
};
